import { cn } from '@/lib/utils'
import type { KeyboardEventHandler, ReactNode } from 'react'
import { useId, useState } from 'react'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select'

export type OpcaoSelect<T = unknown> = {
    /** Valor devolvido no `onValueChange` — número, `null`, booleano, o que for. */
    value: T
    label: ReactNode
    disabled?: boolean
}

export type SelectFieldProps<T> = {
    options: OpcaoSelect<T>[] | undefined
    value: T
    onValueChange: (value: T) => void
    label?: ReactNode
    placeholder?: string
    helperText?: ReactNode
    /** Mensagem de erro (string) ou só a marcação de inválido (boolean). */
    error?: ReactNode | boolean
    required?: boolean
    disabled?: boolean
    /**
     * Não reserva a linha auxiliar abaixo do campo. Padrão nos selects soltos;
     * o `FormFieldSelect` reserva, para que a mensagem de erro não empurre o
     * formulário quando aparece.
     */
    unHelperText?: boolean
    variant?: 'filled' | 'outlined'
    /** Altura do campo: `medium` 56px, `small` 48px. */
    size?: 'small' | 'medium'
    /** Conteúdo à direita do campo, antes da seta. */
    endAdornment?: ReactNode
    className?: string
    id?: string
    'data-testid'?: string
    onBlur?: () => void
    /** Impede a tecla de vazar para o diálogo/guia que envolve o select. */
    onKeyDownCapture?: KeyboardEventHandler
}

/**
 * Comparação frouxa por string: o estado pode
 * guardar `3` enquanto a opção declara `'3'` e a seleção continua sendo exibida.
 */
function mesmoValor(valorOpcao: unknown, valorCampo: unknown) {
    if (typeof valorCampo === 'object' && valorCampo !== null) {
        return valorOpcao === valorCampo
    }

    return String(valorOpcao) === String(valorCampo)
}

/**
 * Select — rótulo dentro da caixa, linha auxiliar
 * opcional — montado a partir de uma lista de opções em vez de `SelectItem`
 * soltos. É a versão sem react-hook-form; para formulário existe o
 * `FormFieldSelect`, que é este componente dentro de um `Controller`.
 *
 * O Radix trabalha apenas com strings, então o índice da opção é usado como
 * chave no DOM e o `value` original é devolvido no `onValueChange`. Isso mantém
 * números, `null` e string vazia funcionando como opção.
 */
export function SelectField<T>({
    options,
    value,
    onValueChange,
    label,
    placeholder,
    helperText,
    error,
    required,
    disabled,
    unHelperText = true,
    variant,
    size,
    endAdornment,
    className,
    id,
    'data-testid': dataTestId,
    onBlur,
    onKeyDownCapture,
}: SelectFieldProps<T>) {
    const idGerado = useId()
    const idGatilho = id ?? idGerado
    const idAuxiliar = `${idGatilho}-auxiliar`
    /**
     * A lista aberta é acompanhada aqui — e não pelo Radix — porque o rótulo
     * precisa encolher enquanto ela está aberta, do mesmo jeito que o Combobox.
     */
    const [aberto, setAberto] = useState(false)

    const opcoes = options ?? []
    const indice = opcoes.findIndex(opcao => mesmoValor(opcao.value, value))
    const temRotulo = label !== undefined && label !== ''
    /**
     * Sem seleção, sem placeholder e com a lista fechada não há nada na linha
     * do valor: o rótulo ocupa o lugar dela.
     */
    const rotuloGrande = temRotulo && indice < 0 && !placeholder && !aberto
    const invalido = error !== undefined && error !== false
    const mensagemErro = typeof error === 'boolean' ? undefined : error
    const auxiliar = mensagemErro ?? helperText

    return (
        <div className={cn('flex w-full flex-col gap-1', className)}>
            <div className="relative">
                <Select
                    open={aberto}
                    onOpenChange={setAberto}
                    value={indice >= 0 ? String(indice) : ''}
                    disabled={disabled}
                    onValueChange={chave => {
                        const opcao = opcoes[Number(chave)]
                        if (!opcao) return

                        onValueChange(opcao.value)
                    }}
                >
                    <SelectTrigger
                        id={idGatilho}
                        data-testid={dataTestId}
                        variant={variant}
                        size={size}
                        onBlur={onBlur}
                        onKeyDownCapture={onKeyDownCapture}
                        aria-invalid={invalido || undefined}
                        aria-required={required || undefined}
                        aria-describedby={idAuxiliar}
                        className={cn(endAdornment && 'pr-12')}
                        rotuloGrande={rotuloGrande}
                        label={
                            temRotulo ? (
                                <>
                                    {label}
                                    {required && (
                                        <span aria-hidden="true">{' *'}</span>
                                    )}
                                </>
                            ) : undefined
                        }
                    >
                        <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent onKeyDownCapture={onKeyDownCapture}>
                        {opcoes.map((opcao, posicao) => (
                            <SelectItem
                                key={posicao}
                                value={String(posicao)}
                                disabled={opcao.disabled}
                            >
                                {opcao.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {endAdornment && (
                    <span
                        aria-disabled={disabled || undefined}
                        className="group absolute inset-y-0 right-8 flex items-center text-muted-foreground"
                    >
                        {endAdornment}
                    </span>
                )}
            </div>
            {(!unHelperText || !!mensagemErro) && (
                <span
                    id={idAuxiliar}
                    className={cn(
                        'px-3 text-xs',
                        invalido ? 'text-destructive' : 'text-muted-foreground',
                    )}
                >
                    {auxiliar ?? ' '}
                </span>
            )}
        </div>
    )
}
